import * as THREE from 'three';

/**
 * Finale - the cinematic climax of the experience.
 *
 * Triggered by clicking the Black Hole. The sequence:
 *
 *   1. DESCENDING (~7s): camera flies from its current position toward
 *      the black hole on a smooth curve, while text beats fade in.
 *      "You have read books about this." → "You have known it was at the
 *      center of yours." → "Tonight you came to look at it directly."
 *
 *   2. EVENT_HORIZON: camera holds. Text:
 *      "At the center of every galaxy is a black hole."
 *      "At the center of yours is this moment."
 *      A "click to cross" prompt appears at the bottom.
 *
 *   3. CROSSING: on the user's click, the screen fades to pure black
 *      over ~2s. Music would swell here (Mikrokosmos - not yet wired).
 *
 *   4. REVEAL: the cosmic message and the closing line fade in line by
 *      line over ~25s.
 *
 *   5. POST_FINALE: the overlay clears, camera unlocks, free movement
 *      restored. The visitor returns to her universe.
 *
 * The class manages its own DOM elements, timers, click handling, and
 * camera animation. It runs each frame via update(dt) called by Game.
 */

const DESCENT_DURATION = 7;        // seconds - camera flies toward BH
const EVENT_HORIZON_DISTANCE = 65; // world units - how close to stop
const CROSSING_DURATION = 2.5;     // seconds - fade to black
const REVEAL_HOLD = 6;             // seconds the closing message holds before exit
const RETURN_DURATION = 4.5;       // seconds - camera glides back to its origin

export class Finale {
  constructor({ camera, blackHole, onComplete }) {
    this.camera = camera;
    this.blackHole = blackHole;
    this.onComplete = onComplete;

    this.state = 'idle'; // idle | descending | event_horizon | crossing | reveal | returning | done
    this.elapsed = 0;
    this.descentT = 0;
    this.crossingT = 0;
    this.returnT = 0;
    this.timers = [];

    this.originalCameraPos = new THREE.Vector3();
    this.originalCameraQuat = new THREE.Quaternion();
    this.targetCameraPos = new THREE.Vector3();
    this.targetCameraQuat = new THREE.Quaternion();
    this.returnFromPos = new THREE.Vector3();
    this.returnFromQuat = new THREE.Quaternion();
    this.blackHolePos = new THREE.Vector3();

    this.overlay = document.getElementById('finale-overlay');
    this.text = document.getElementById('finale-text');
    this.bigText = document.getElementById('finale-big-text');
    this.closingText = document.getElementById('finale-closing');
    this.prompt = document.getElementById('finale-prompt');
  }

  start() {
    if (this.state !== 'idle') return;

    if (!this.overlay) {
      console.error('Finale: missing overlay elements in DOM');
      this.complete();
      return;
    }

    this.overlay.classList.add('active');

    // Lock camera + save its current pose (position AND orientation)
    this.camera.locked = true;
    this.originalCameraPos.copy(this.camera.three.position);
    this.originalCameraQuat.copy(this.camera.three.quaternion);

    // Compute target position: just outside the black hole's event horizon,
    // along the line from camera to black hole
    this.blackHolePos.copy(this.blackHole.mesh.position);
    const direction = this.blackHolePos.clone().sub(this.originalCameraPos).normalize();
    this.targetCameraPos
      .copy(this.blackHolePos)
      .sub(direction.multiplyScalar(EVENT_HORIZON_DISTANCE));

    // Compute the target quaternion (camera at targetCameraPos, looking
    // at the black hole). We do this by cloning the camera, positioning
    // it at the target, calling lookAt, and reading off the quaternion.
    // We'll slerp from originalCameraQuat → targetCameraQuat during the
    // descent so the orientation change is smooth, not a snap.
    const tempCam = this.camera.three.clone();
    tempCam.position.copy(this.targetCameraPos);
    tempCam.lookAt(this.blackHolePos);
    tempCam.updateMatrixWorld();
    this.targetCameraQuat.copy(tempCam.quaternion);

    // Bind click handler now (used during EVENT_HORIZON)
    this._onClick = (e) => {
      if (this.state === 'event_horizon' && this._promptVisible) {
        this.startCrossing();
      }
    };
    document.addEventListener('click', this._onClick);

    // Begin descent
    this.startDescent();
  }

  startDescent() {
    this.state = 'descending';
    this.descentT = 0;

    const at = (ms, fn) => {
      this.timers.push(setTimeout(() => {
        if (this.state === 'descending') fn();
      }, ms));
    };

    at(500,  () => this.showText('You have read books about this.'));
    at(3000, () => this.showText('You have known this thing was at the center of every galaxy.'));
    at(5500, () => this.showText('You have known it was at the center of yours.'));

    // After descent completes, hold a moment then go to event horizon
    this.timers.push(setTimeout(() => {
      if (this.state === 'descending') this.startEventHorizon();
    }, (DESCENT_DURATION + 0.5) * 1000));
  }

  startEventHorizon() {
    this.state = 'event_horizon';
    this.hideText();

    this.timers.push(setTimeout(() => {
      if (this.state === 'event_horizon') {
        this.showText('At the center of every galaxy is a black hole.');
      }
    }, 1500));

    this.timers.push(setTimeout(() => {
      if (this.state === 'event_horizon') {
        this.showText('At the center of yours is this moment.');
      }
    }, 5500));

    this.timers.push(setTimeout(() => {
      if (this.state === 'event_horizon') {
        this.showPrompt();
      }
    }, 9500));
  }

  startCrossing() {
    this.state = 'crossing';
    this.crossingT = 0;
    this.hideText();
    this.hidePrompt();

    // Fade overlay to pure black
    this.overlay.classList.add('crossing');

    this.timers.push(setTimeout(() => {
      if (this.state === 'crossing') this.startReveal();
    }, CROSSING_DURATION * 1000));
  }

  startReveal() {
    this.state = 'reveal';
    this.overlay.classList.add('black');

    // Cosmic birthday message
    this.timers.push(setTimeout(() => this.showBigText('Happy Birthday, Jups <3'), 1500));

    // Closing line, fades in line by line on top of the big text
    const closingLines = [
      'You are 28 years of starlight.',
      'Every step you have danced, every place you have gone, every person you have met, every black hole you have read about - all of it has been arriving at you at once.',
      'This is what you are made of.',
      'This is your universe. Go & explore.',
    ];

    let baseDelay = 6500;
    closingLines.forEach((line, i) => {
      this.timers.push(setTimeout(() => {
        this.appendClosingLine(line);
      }, baseDelay + i * 4500));
    });

    // After the closing line has held, transition to the 'returning'
    // phase: camera glides back to its original pose while the overlay
    // fades out.
    const totalRevealMs = baseDelay + closingLines.length * 4500 + REVEAL_HOLD * 1000;
    this.timers.push(setTimeout(() => {
      this.startReturn();
    }, totalRevealMs));
  }

  startReturn() {
    this.state = 'returning';
    this.returnT = 0;
    this.returnFromPos.copy(this.camera.three.position);
    this.returnFromQuat.copy(this.camera.three.quaternion);

    // Fade out overlay + reveal text
    this.overlay.classList.add('fading-out');
    if (this.bigText) this.bigText.classList.remove('visible');
    if (this.closingText) this.closingText.classList.remove('visible');

    // After the camera return completes, fully unlock and end the finale
    this.timers.push(setTimeout(() => {
      this.complete();
    }, RETURN_DURATION * 1000));
  }

  complete() {
    if (this.state === 'done') return;
    this.state = 'done';
    this.timers.forEach(clearTimeout);
    this.timers = [];

    document.removeEventListener('click', this._onClick);

    if (this.overlay) {
      this.overlay.classList.remove('active', 'crossing', 'black', 'fading-out');
    }
    if (this.text) this.text.classList.remove('visible');
    if (this.bigText) {
      this.bigText.classList.remove('visible');
      this.bigText.textContent = '';
    }
    if (this.closingText) {
      this.closingText.classList.remove('visible');
      this.closingText.innerHTML = '';
    }
    if (this.prompt) this.prompt.classList.remove('visible');
    this._promptVisible = false;

    // Restore camera control
    this.camera.locked = false;

    if (this.onComplete) this.onComplete();
  }

  showText(text) {
    if (!this.text) return;
    this.text.classList.remove('visible');
    setTimeout(() => {
      if (this.state === 'done') return;
      this.text.textContent = text;
      void this.text.offsetHeight;
      this.text.classList.add('visible');
    }, 950);
  }

  hideText() {
    if (!this.text) return;
    this.text.classList.remove('visible');
  }

  showBigText(text) {
    if (!this.bigText) return;
    this.bigText.textContent = text;
    void this.bigText.offsetHeight;
    this.bigText.classList.add('visible');
  }

  appendClosingLine(text) {
    if (!this.closingText) return;
    const p = document.createElement('p');
    p.textContent = text;
    p.className = 'closing-line';
    this.closingText.appendChild(p);
    this.closingText.classList.add('visible');
    // Trigger CSS animation on the new line
    void p.offsetHeight;
    p.classList.add('visible');
  }

  showPrompt() {
    if (!this.prompt) return;
    this.prompt.textContent = '[ click to cross ]';
    this.prompt.classList.add('visible');
    this._promptVisible = true;
  }

  hidePrompt() {
    if (!this.prompt) return;
    this.prompt.classList.remove('visible');
    this._promptVisible = false;
  }

  /**
   * Called every frame by Game (after Camera.update so we can override
   * the camera's position and look direction).
   *
   * All orientation changes are done via quaternion slerp instead of
   * Camera.lookAt() - lookAt snaps the camera, slerp interpolates.
   */
  update(dt) {
    if (this.state === 'descending') {
      this.descentT = Math.min(1, this.descentT + dt / DESCENT_DURATION);
      const eased = easeInOutCubic(this.descentT);
      this.camera.three.position.lerpVectors(
        this.originalCameraPos,
        this.targetCameraPos,
        eased,
      );
      this.camera.three.quaternion.slerpQuaternions(
        this.originalCameraQuat,
        this.targetCameraQuat,
        eased,
      );
    } else if (this.state === 'event_horizon') {
      // Hold at the target pose
      this.camera.three.position.copy(this.targetCameraPos);
      this.camera.three.quaternion.copy(this.targetCameraQuat);
    } else if (this.state === 'crossing') {
      this.crossingT += dt;
      const t = Math.min(1, this.crossingT / CROSSING_DURATION);
      const closer = this.targetCameraPos.clone().lerp(this.blackHolePos, t * 0.5);
      this.camera.three.position.copy(closer);
      this.camera.three.quaternion.copy(this.targetCameraQuat);
    } else if (this.state === 'reveal') {
      // Held at the moment of crossing
      this.camera.three.quaternion.copy(this.targetCameraQuat);
    } else if (this.state === 'returning') {
      this.returnT = Math.min(1, this.returnT + dt / RETURN_DURATION);
      const eased = easeInOutCubic(this.returnT);
      this.camera.three.position.lerpVectors(
        this.returnFromPos,
        this.originalCameraPos,
        eased,
      );
      this.camera.three.quaternion.slerpQuaternions(
        this.returnFromQuat,
        this.originalCameraQuat,
        eased,
      );
    }
  }
}

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}
